/**
 * English: Load /api/agents map for the active station (device agents + manager metadata).
 * Thai: โหลด map /api/agents ของสถานีที่เลือก (device agents + metadata ของ manager)
 */
import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { ENDPOINTS } from '../config/api';
import { Agent, AgentsMap } from '../types';

/** Align with backend shared/device_sort: type A–Z, then device_id natural (a-2 before a-10). */
function compareAgentsDisplayOrder(a: Agent, b: Agent): number {
  const typeA = String(a.role || a.device_type || '').toLowerCase();
  const typeB = String(b.role || b.device_type || '').toLowerCase();
  if (typeA !== typeB) {
    return typeA.localeCompare(typeB, 'en', { sensitivity: 'base' });
  }
  const idA = String((a as Agent & { device_id?: string }).device_id || a.name || a.id);
  const idB = String((b as Agent & { device_id?: string }).device_id || b.name || b.id);
  return idA.localeCompare(idB, 'en', { numeric: true, sensitivity: 'base' });
}

interface UseAgentsResult {
  agents: AgentsMap;
  agentList: Agent[];
  deviceAgents: Agent[];
  manager: Agent | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useAgents(stationId: string): UseAgentsResult {
  const { get } = useApi();
  const [agents, setAgents] = useState<AgentsMap>({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    try {
      const data = (await get(ENDPOINTS.stationAgents(stationId))) as { agents?: AgentsMap };
      setAgents(data.agents || {});
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  }, [get, stationId]);

  useEffect(() => {
    if (!stationId) {
      setAgents({});
      return;
    }
    void refresh();
  }, [stationId, refresh]);

  const agentList = Object.values(agents);
  const deviceAgents = agentList.filter(a => a.id !== 'manager').sort(compareAgentsDisplayOrder);
  const manager = agents.manager || null;

  return { agents, agentList, deviceAgents, manager, loading, refresh };
}
